import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin, Youtube } from "lucide-react";
import { XIcon } from "@/components/site/XIcon";
import { cn } from "@/lib/utils";

import logoHorizontal from "@/assets/logo-horizontal.svg";
import iso13485Logo from "@/assets/compliance/iso13485.png?format=webp&w=300&quality=80";
import gdprLogo from "@/assets/compliance/gdpr.png?format=webp&w=300&quality=80";
import isoLogo from "@/assets/compliance/iso27001.png?format=webp&w=300&quality=80";
import hipaaLogo from "@/assets/compliance/hipaa.webp";
import bsiC5Logo from "@/assets/compliance/bsi-c5.png?format=webp&w=300&quality=80";


const columns = [
  {
    label: "Navigate",
    links: [
      { label: "Solution", href: "/solution" },
      { label: "Insights", href: "/insights" },
      { label: "FAQ", href: "/faq" },
    ],
  },
  {
    label: "Company",
    links: [
      { label: "Company", href: "/company" },
      { label: "Careers", href: "/company/careers" },
      { label: "Events", href: "/events" },
      { label: "Contact", href: "mailto:contact@deepc.ai" },
    ],
  },
];

const compliance = [
  { label: "ISO 13485:2016", src: iso13485Logo },
  { label: "GDPR", src: gdprLogo },
  { label: "ISO 27001", src: isoLogo },
  { label: "HIPAA-aligned", src: hipaaLogo },
  { label: "BSI C5 Testat", src: bsiC5Logo },
];

const linkClass = cn(
  "group/link relative inline-block text-sm text-seabed-foreground/75 transition-colors duration-300 hover:text-seabed-foreground",
);

const FooterLink = ({ href, label }: { href: string; label: string }) => {
  const isExternal = /^(https?:|mailto:|tel:)/.test(href);
  const underline = (
    <span
      aria-hidden
      className="pointer-events-none absolute left-0 -bottom-0.5 h-px w-full origin-left scale-x-0 bg-primary transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover/link:scale-x-100"
    />
  );
  if (isExternal) {
    return (
      <a href={href} className={linkClass}>
        {label}
        {underline}
      </a>
    );
  }
  return (
    <Link to={href} className={linkClass}>
      {label}
      {underline}
    </Link>
  );
};

const Footer = forwardRef<HTMLElement>((_, ref) => {
  return (
    <footer
      ref={ref}
      className="relative bg-seabed text-seabed-foreground overflow-hidden"
    >
      {/* Primary gradient hairline at top edge */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px"
        style={{ background: "var(--gradient-primary-line)" }}
      />
      {/* Ambient primary glow behind brand */}
      <div
        aria-hidden
        className="absolute -left-32 -top-24 h-[480px] w-[640px] pointer-events-none opacity-[0.55]"
        style={{
          background:
            "radial-gradient(ellipse 60% 60% at 30% 30%, hsl(var(--primary) / 0.08), transparent 70%)",
        }}
      />

      <div className="container relative py-section-tight">
        <div className="grid grid-cols-1 md:grid-cols-12 md:auto-rows-min gap-x-10 md:gap-x-6 lg:gap-x-12 gap-y-12">
          {/* Brand */}
          <div className="md:col-span-6 md:row-start-1 lg:col-span-4 lg:row-start-1">
            <Link
              to="/"
              className="inline-flex items-center text-seabed-foreground"
              aria-label="deepc home"
            >
              <img src={logoHorizontal} alt="deepc" width={140} height={28} className="h-7 w-auto" />
            </Link>
            <p className="mt-6 text-sm text-seabed-foreground/65 leading-relaxed max-w-xs">
              Clinical AI infrastructure for health systems.
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-4">
              {compliance.map((c) => (
                <img
                  key={c.label}
                  src={c.src}
                  alt={c.label}
                  loading="lazy"
                  width={80}
                  height={53}
                  className="h-[53px] w-auto object-contain"
                />
              ))}
            </div>

            <p className="mt-5 text-[11px] uppercase tracking-eyebrow text-seabed-foreground/55 font-medium">
              Regions <span className="text-seabed-foreground/40">·</span>{" "}
              <span className="text-seabed-foreground/75 normal-case tracking-normal text-xs">
                US · EU · UK · ME
              </span>
            </p>

            <div className="mt-7 flex items-center gap-2">
              <a
                href="https://www.linkedin.com/company/deepchealth"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="deepc on LinkedIn"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-seabed-foreground/15 text-seabed-foreground/75 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:text-seabed-foreground hover:border-primary/50 hover:bg-seabed-foreground/[0.04]"
              >
                <Linkedin className="h-4 w-4" />
              </a>
              <a
                href="https://x.com/deepchealth"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="deepc on X"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-seabed-foreground/15 text-seabed-foreground/75 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:text-seabed-foreground hover:border-primary/50 hover:bg-seabed-foreground/[0.04]"
              >
                <XIcon className="h-4 w-4" />
              </a>
              <a
                href="https://www.youtube.com/channel/UCJelTnuYEARgt8A-adxs6LQ/videos"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="deepc on YouTube"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-seabed-foreground/15 text-seabed-foreground/75 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:text-seabed-foreground hover:border-primary/50 hover:bg-seabed-foreground/[0.04]"
              >
                <Youtube className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Nav columns */}
          <div className="md:col-span-6 md:row-start-2 pt-10 border-t border-seabed-line lg:pt-0 lg:border-t-0 lg:col-start-5 lg:col-span-3 lg:row-start-1 grid grid-cols-2 gap-8">
            {columns.map((col) => (
              <div key={col.label}>
                <p className="text-[11px] uppercase tracking-eyebrow text-seabed-foreground/45 font-medium">
                  {col.label}
                </p>
                <ul className="mt-5 space-y-3">
                  {col.links.map((link) => (
                    <li key={link.href}>
                      <FooterLink href={link.href} label={link.label} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Newsletter CTA */}
          <div className="md:col-span-12 md:row-start-3 lg:col-start-8 lg:col-span-5 lg:row-start-1">
            <div
              className="relative overflow-hidden rounded-2xl border border-primary/30 p-8"
              style={{
                background:
                  "linear-gradient(135deg, hsl(var(--primary) / 0.18) 0%, hsl(var(--primary) / 0.06) 55%, transparent 100%)",
              }}
            >
              <div
                aria-hidden
                className="pointer-events-none absolute -top-24 -right-20 h-64 w-64 rounded-full opacity-60"
                style={{
                  background:
                    "radial-gradient(circle, hsl(var(--primary) / 0.35), transparent 70%)",
                }}
              />
              <div
                aria-hidden
                className="absolute inset-x-0 top-0 h-px"
                style={{ background: "var(--gradient-primary-line)" }}
              />
              <p className="relative text-[11px] uppercase tracking-eyebrow text-primary font-medium">
                Newsletter
              </p>
              <h3 className="relative mt-3 text-2xl font-medium text-seabed-foreground leading-snug max-w-sm">
                Field notes from the clinical AI frontier.
              </h3>
              <p className="relative mt-3 text-sm text-seabed-foreground/75 leading-relaxed max-w-sm">
                Monthly briefings on deployments, evidence, and what's shipping across the network.
              </p>
              <Link
                to="/form/newsletter"
                className="group/cta relative mt-7 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.6)] transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-primary/90 hover:shadow-[0_14px_40px_-10px_hsl(var(--primary)/0.75)]"
              >
                Join the newsletter
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5" />
              </Link>
            </div>
          </div>

          {/* Legal — full width bottom row */}
          <div className="md:col-span-12 md:row-start-4 lg:col-span-12 lg:row-start-2">
            <div className="flex flex-wrap gap-6 text-xs text-seabed-foreground/75">
              <FooterLink href="/legal/privacy" label="Privacy" />
              <FooterLink href="/legal/imprint" label="Imprint" />
              <FooterLink href="/legal/vulnerability-reporting" label="Security" />
            </div>

            <p className="mt-6 pt-6 border-t border-seabed-line text-[10px] font-thin text-seabed-foreground/100 leading-relaxed">
              <span className="font-medium text-seabed-foreground/75">Disclaimer:</span>{" "}
              AI results are provided by external regulatory-approved AI solutions/partners. deepcOS communicates and displays clinical information to end-users. Availability and regulatory status of medical devices accessible via deepcOS vary by jurisdiction. Please contact deepc for information applicable to your territory. (
              <a href="mailto:contact@deepc.ai" className="hover:text-seabed-foreground">contact@deepc.ai</a>
              ).
            </p>

            <p className="mt-6 pt-6 border-t border-seabed-line text-xs text-seabed-foreground/55">
              © {new Date().getFullYear()} deepc. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = "Footer";

export default Footer;
